import { OperatorNodeModel } from "@/models/nodes/OperatorNodeModel";

import type { NodeModel } from "@/models/nodes/types";
import type { SignatureEntry } from "@corca/data";

/**
 * A quick check to skip notations that clearly can't match, before doing the real (more
 * expensive) matching. Returns true if the notation's fixed parts (its `/`, power, etc. —
 * everything that isn't a blank) all show up in `nodes`, in order.
 *
 * It can say yes and still turn out not to match — a blank can stand for several nodes, so a
 * notation with 3 fixed parts can match a longer expression like `x²/y²`. That's fine: this only
 * weeds out the obvious misses, and `matchVariant` makes the final decision.
 */
export function isSignatureMatch(signature: SignatureEntry[], nodes: NodeModel[]): boolean {
  let ni = 0;

  for (const entry of signature) {
    if (entry === "placeholder") {
      continue;
    }

    while (ni < nodes.length && !entryMatchesNode(entry, nodes[ni])) {
      ni++;
    }

    if (ni >= nodes.length) {
      return false;
    }

    ni++;
  }

  return true;
}

/**
 * The signature entry a real node would produce (`operator:power`, `variable:x`, …).
 */
export function nodeSignatureEntry(node: NodeModel): SignatureEntry {
  const qualifier = getNodeQualifier(node);
  return (qualifier ? `${node.type}:${qualifier}` : node.type) as SignatureEntry;
}

function entryMatchesNode(entry: SignatureEntry, node: NodeModel): boolean {
  const [type, qualifier] = entry.split(":");

  if (type !== node.type) {
    return false;
  }

  return !qualifier || qualifier === getNodeQualifier(node);
}

export function getNodeQualifier(node: NodeModel): string | undefined {
  if (node.is(OperatorNodeModel)) {
    return node.operatorType;
  }

  return "value" in node ? node.value : undefined;
}
